/**
 * Word Improvement page component for ClarCV application.
 * 
 * Allows users to upload their existing Word CV (.docx) and get
 * AI-powered content improvements while optionally preserving the original layout.
 * 
 * @component
 * @returns {JSX.Element} Word Improvement page component
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import TemplateGallery from '../components/TemplateGallery';
import AnalyzingProgress from '../components/AnalyzingProgress';
import { useLanguage } from '../contexts/LanguageContext';
import { useRequireAuth } from '../contexts/AuthGateContext';
import { buildCvDownloadFilename } from '../utils/downloadFilename';
import { usePlanLimitPrompt } from '../hooks/usePlanLimitPrompt';

/**
 * Word Improvement component that handles Word upload, analysis, and improvement.
 * 
 * @returns {JSX.Element} The Word improvement interface with upload and analysis
 */
const WordImprovement = () => {
  const { t } = useLanguage();
  const requireAuth = useRequireAuth();
  const { handlePlanLimitError, prompt: upgradePrompt } = usePlanLimitPrompt();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [step, setStep] = useState(1); // 1: Upload, 2: Analysis, 3: Results
  // Format modes: 'free', 'extract_styles', 'preserve_exact'
  const [formatMode, setFormatMode] = useState('extract_styles'); // Default to extract styles
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const [aiConsent, setAiConsent] = useState(false);

  /**
   * Handle file selection
   */
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Selecciona un archivo Word (.docx)');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('El archivo debe pesar menos de 10 MB');
        return;
      }
      setFile(selectedFile);
      toast.success('Archivo Word seleccionado correctamente');
    }
  };

  /**
   * Handle file upload and analysis
   */
  const handleUploadAndAnalyze = async () => {
    if (!file) {
      toast.error('Selecciona primero un archivo Word');
      return;
    }
    if (!aiConsent) {
      toast.error('Acepta el consentimiento de uso de IA para continuar.');
      return;
    }
    if (!(await requireAuth())) {
      return;
    }

    setUploading(true);
    setStep(2);

    try {
      // Upload file to backend for analysis
      const formData = new FormData();
      formData.append('pdf_file', file); // Backend expects 'pdf_file' even for Word
      formData.append('analysis_type', 'improvement');
      formData.append('ai_consent', 'true');

      const analyzingToastId = toast.info('Analizando el contenido Word con IA...', {
        autoClose: false,
      });
      
      // Call API endpoint for Word analysis (same as PDF)
      const response = await axios.post('/api/cvs/analyze-pdf/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('DEBUG: Analysis response:', response.data);
      console.log('DEBUG: original_file_ref received:', response.data.original_file_ref);
      setAnalysis(response.data);
      setStep(3);
      
      toast.update(analyzingToastId, {
        render: 'Análisis completado',
        type: 'success',
        autoClose: 3000,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error analyzing Word:', error);
      if (await handlePlanLimitError(error)) {
        setStep(1);
        return;
      }
      
      // Check for OpenAI specific errors
      if (error.response?.data?.error_type === 'openai_error') {
        const errorMessage = error.response.data.error;
        const step = error.response.data.step || 'processing';
        
        if (errorMessage.includes('quota exceeded') || errorMessage.includes('insufficient_quota')) {
          toast.error(
            <div>
              <strong>❌ Cuota de la API de OpenAI agotada</strong>
              <p style={{ marginTop: '4px', fontSize: '0.85em' }}>
                Revisa tu facturación de OpenAI en <a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>platform.openai.com</a>
              </p>
            </div>,
            { autoClose: 8000 }
          );
        } else if (errorMessage.includes('Invalid') || errorMessage.includes('authentication')) {
          toast.error(
            <div>
              <strong>❌ Clave de API de OpenAI no válida</strong>
              <p style={{ marginTop: '4px', fontSize: '0.85em' }}>
                Revisa la configuración de tu clave de API
              </p>
            </div>,
            { autoClose: 6000 }
          );
        } else {
          toast.error(`Error de OpenAI durante ${step}: ${errorMessage}`);
        }
      } else {
        toast.error(error.response?.data?.error || 'Error al analizar el archivo Word. Inténtalo de nuevo.');
      }
      
      setStep(1);
    } finally {
      setUploading(false);
      setAnalyzing(false);
    }
  };

  /**
   * Handle improved Word generation - just marks as ready, download happens via buttons
   */
  const handleGenerateImprovedWord = async () => {
    if (!analysis) {
      toast.error('No hay datos de análisis disponibles');
      return;
    }

    // Just move to step 4 where download buttons are shown
    setStep(4);
    toast.success('✅ ¡CV mejorado! Elige el formato de descarga abajo.');
  };

  /**
   * Download document in specified format
   */
  const handleDownload = async (format) => {
    if (!analysis) {
      toast.error('No hay datos de análisis disponibles');
      return;
    }
    if (!(await requireAuth())) {
      return;
    }

    setAnalyzing(true);
    const formatLabel = format === 'docx' ? 'Word' : 'PDF';
    toast.info(`Generando documento ${formatLabel}...`);

    try {
      const requestData = {
        analysis_data: analysis,
        output_format: format,
        format_mode: formatMode  // Use new format_mode parameter
      };

      console.log(`DEBUG: Generating ${format} with format_mode:`, formatMode);
      console.log('DEBUG: Sending original_file_ref:', analysis.original_file_ref);

      const response = await axios.post(
        '/api/cvs/generate-improved-cv/',
        requestData,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // Create blob and download
      const mimeTypes = {
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'pdf': 'application/pdf'
      };
      
      const blob = new Blob([response.data], { type: mimeTypes[format] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const originalName = file.name.replace(/\.(docx|pdf)$/i, '');
      link.download = buildCvDownloadFilename({
        name: originalName,
        extension: format,
      });
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`✅ Documento ${formatLabel} descargado correctamente`);
    } catch (error) {
      console.error(`Error generating ${format}:`, error);
      if (await handlePlanLimitError(error)) {
        return;
      }
      toast.error(error.response?.data?.error || `Error al generar ${formatLabel}. Inténtalo de nuevo.`);
    } finally {
      setAnalyzing(false);
    }
  };

  /**
   * Handle starting over
   */
  const handleStartOver = () => {
    setFile(null);
    setAnalysis(null);
    setStep(1);
    setFormatMode('extract_styles');  // Reset to default
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📝</div>
        <h1 style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1d4ed8', marginBottom: '0.5rem' }}>
          Mejorar Word
        </h1>
        <p style={{ fontSize: '1.1rem', color: '#6b7280' }}>
          Sube tu CV en Word y mejóralo con IA manteniendo el diseño
        </p>
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '16px',
          padding: '3rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          border: '2px solid #e5e7eb'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1.5rem', textAlign: 'center' }}>
            Sube tu CV en Word
          </h2>

          {/* File Upload Area */}
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '3px dashed #cbd5e1',
              borderRadius: '12px',
              padding: '3rem 2rem',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: file ? '#f0fdf4' : '#f8fafc',
              transition: 'all 0.3s ease',
              marginBottom: '2rem'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.backgroundColor = '#eff6ff';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#cbd5e1';
              e.target.style.backgroundColor = file ? '#f0fdf4' : '#f8fafc';
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
              {file ? '✅' : '📤'}
            </div>
            
            {file ? (
              <>
                <p style={{ fontSize: '1.1rem', fontWeight: '600', color: '#059669', marginBottom: '0.5rem' }}>
                  {file.name}
                </p>
                <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </>
            ) : (
              <>
                <p style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                  Haz clic para seleccionar el archivo Word
                </p>
                <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                  o arrastra y suelta tu archivo .docx aquí
                </p>
              </>
            )}
          </div>

          {/* AI consent */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.9rem', color: '#4b5563' }}>
              <input
                type="checkbox"
                checked={aiConsent}
                onChange={(e) => setAiConsent(e.target.checked)}
                style={{ marginTop: '0.15rem' }}
              />
              <span>
                Entiendo y acepto que el contenido de mi CV se envía a OpenAI solo para generar el análisis o la mejora. No se usa para entrenar modelos de IA ni se vende.
              </span>
            </label>
          </div>

          {/* Format Mode Selection */}
          <div style={{
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
              🎨 Elige el modo de formato
            </h3>
            
            {/* Format Mode Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              
              {/* Option 1: Free Improvement */}
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: '1rem',
                backgroundColor: formatMode === 'free' ? '#f0fdf4' : '#fff',
                border: `2px solid ${formatMode === 'free' ? '#059669' : '#e2e8f0'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="radio"
                  name="formatMode"
                  value="free"
                  checked={formatMode === 'free'}
                  onChange={(e) => setFormatMode(e.target.value)}
                  style={{ marginTop: '0.25rem', marginRight: '1rem', cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                    🚀 Mejora libre — Nuevo diseño profesional
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    Crea un CV completamente nuevo con estilos profesionales predefinidos. ¡Ideal para máximo impacto!
                  </div>
                </div>
              </label>

              {/* Option 2: Extract Styles (Default) */}
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: '1rem',
                backgroundColor: formatMode === 'extract_styles' ? '#f0f9ff' : '#fff',
                border: `2px solid ${formatMode === 'extract_styles' ? '#3b82f6' : '#e2e8f0'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="radio"
                  name="formatMode"
                  value="extract_styles"
                  checked={formatMode === 'extract_styles'}
                  onChange={(e) => setFormatMode(e.target.value)}
                  style={{ marginTop: '0.25rem', marginRight: '1rem', cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                    🎨 Extraer estilos — Conserva colores y tipografías
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    Extrae colores y tipografías de tu CV original y aplícalos al contenido mejorado. (Recomendado)
                  </div>
                </div>
              </label>

              {/* Option 3: Preserve Exact Format */}
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: '1rem',
                backgroundColor: formatMode === 'preserve_exact' ? '#fef3c7' : '#fff',
                border: `2px solid ${formatMode === 'preserve_exact' ? '#f59e0b' : '#e2e8f0'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="radio"
                  name="formatMode"
                  value="preserve_exact"
                  checked={formatMode === 'preserve_exact'}
                  onChange={(e) => setFormatMode(e.target.value)}
                  style={{ marginTop: '0.25rem', marginRight: '1rem', cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                    📋 Conservar formato exacto — Solo cambios de texto
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    Mantén el formato original exacto. Solo se mejorará el texto. Ideal si te encanta tu diseño actual.
                  </div>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#dc2626',
                    backgroundColor: '#fef2f2',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid #fecaca'
                  }}>
                    ⚠️ <strong>Nota:</strong> Este modo NO funciona con CVs que usen cuadros de texto o formas complejas.
                    Si tu CV los usa, el sistema pasará automáticamente al modo «Mejora libre».
                  </div>
                </div>
              </label>
            </div>
            
            {/* Info Box */}
            <div style={{
              marginTop: '1rem',
              backgroundColor: formatMode === 'free' ? '#ecfdf5' : formatMode === 'extract_styles' ? '#eff6ff' : '#fef3c7',
              border: `1px solid ${formatMode === 'free' ? '#059669' : formatMode === 'extract_styles' ? '#3b82f6' : '#f59e0b'}`,
              borderRadius: '8px',
              padding: '0.75rem',
              fontSize: '0.85rem',
              color: formatMode === 'free' ? '#047857' : formatMode === 'extract_styles' ? '#1e40af' : '#d97706'
            }}>
              {formatMode === 'free' && (
                <div>
                  ✨ <strong>Ideal para:</strong> CVs con diseños simples, basados en texto, o si quieres un look profesional nuevo.
                  <br/>Funciona con TODOS los tipos de documento, incluidos los que tienen cuadros de texto.
                </div>
              )}
              {formatMode === 'extract_styles' && (
                <div>
                  🎨 <strong>Ideal para:</strong> CVs Word estándar con párrafos y formato habitual.
                  <br/>Extrae tu esquema de color y tipografías para crear una versión mejorada similar.
                </div>
              )}
              {formatMode === 'preserve_exact' && (
                <div>
                  ⚠️ <strong>Solo funciona con:</strong> Documentos Word simples sin cuadros de texto, formas o diseños complejos.
                  <br/>Si tu CV usa cuadros de texto, el sistema usará «Mejora libre».
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/dashboard/fast-results')}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: '#6b7280',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
            >
              ← Volver
            </button>
            
            <button
              onClick={handleUploadAndAnalyze}
              disabled={!file || uploading || !aiConsent}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: file && !uploading && aiConsent ? '#3b82f6' : '#cbd5e1',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: file && !uploading && aiConsent ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (file && !uploading && aiConsent) e.target.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                if (file && !uploading && aiConsent) e.target.style.backgroundColor = '#3b82f6';
              }}
            >
              {uploading ? '⏳ Analizando...' : '🚀 Analizar y mejorar'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Analyzing */}
      {step === 2 && (
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '16px',
          padding: '2.5rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          border: '2px solid #e5e7eb'
        }}>
          <AnalyzingProgress
            active
            title={t('flow.analyzeProgressTitle')}
            steps={[
              t('flow.analyzeStep1'),
              t('flow.analyzeStep2'),
              t('flow.analyzeStep3'),
              t('flow.analyzeStep4'),
            ]}
          />
        </div>
      )}

      {/* Template Gallery Modal */}
      {showTemplateGallery && analysis && (
        <TemplateGallery
          analysisData={analysis}
          onTemplateSelect={(template) => {
            setShowTemplateGallery(false);
            toast.success(`Plantilla ${template.name} aplicada correctamente`);
          }}
          onClose={() => setShowTemplateGallery(false)}
        />
      )}

      {/* Step 3: Results & Generation */}
      {step === 3 && analysis && (
        <div>
          {/* Analysis Results */}
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '2rem',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '2px solid #e5e7eb',
            marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>
                📊 Resultados del análisis
              </h2>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => navigate('/dashboard/edit-cv', { state: { analysisData: analysis } })}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#047857';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#059669';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  ✏️ Editar secciones
                </button>
                <button
                  onClick={() => setShowTemplateGallery(true)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#1e3a8a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#1e40af';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#1e3a8a';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  🎨 Elegir plantilla
                </button>
              </div>
            </div>

            {/* Score Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              <div style={{
                backgroundColor: '#eff6ff',
                borderRadius: '12px',
                padding: '1.5rem',
                textAlign: 'center',
                border: '2px solid #3b82f6'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1d4ed8', marginBottom: '0.5rem' }}>
                  {analysis.original_score || 75}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>Puntuación original</div>
              </div>
              
              <div style={{
                backgroundColor: '#f0fdf4',
                borderRadius: '12px',
                padding: '1.5rem',
                textAlign: 'center',
                border: '2px solid #059669'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#059669', marginBottom: '0.5rem' }}>
                  {analysis.improved_score || 90}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>Puntuación mejorada</div>
              </div>
              
              <div style={{
                backgroundColor: '#fef3c7',
                borderRadius: '12px',
                padding: '1.5rem',
                textAlign: 'center',
                border: '2px solid #f59e0b'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#d97706', marginBottom: '0.5rem' }}>
                  {analysis.improvements?.length || 0}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>Mejoras</div>
              </div>
            </div>

            {/* Improvements List */}
            {analysis.improvements && analysis.improvements.length > 0 && (
              <div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
                  🎯 Mejoras clave
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {analysis.improvements.map((improvement, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        padding: '1rem',
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ fontWeight: '600', color: '#1f2937' }}>
                          {improvement.section}
                        </span>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          backgroundColor: improvement.impact === 'High' ? '#fecaca' : improvement.impact === 'Medium' ? '#fed7aa' : '#d1fae5',
                          color: improvement.impact === 'High' ? '#991b1b' : improvement.impact === 'Medium' ? '#9a3412' : '#065f46'
                        }}>
                          Impacto: {improvement.impact}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                        <strong>Problema:</strong> {improvement.issue}
                      </p>
                      <p style={{ fontSize: '0.9rem', color: '#059669' }}>
                        <strong>✓ Solución:</strong> {improvement.suggestion}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Generate Improved Word */}
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '2rem',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '2px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>
              📝 Generar documento Word mejorado
            </h2>
            
            <p style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '2rem' }}>
              {formatMode === 'free' && '🚀 Tu CV mejorado usará un diseño profesional nuevo'}
              {formatMode === 'extract_styles' && '🎨 Tu CV mejorado usará tus colores y tipografías originales'}
              {formatMode === 'preserve_exact' && '✨ Tu CV mejorado mantendrá el diseño original exacto'}
            </p>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={handleStartOver}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
              >
                🔄 Empezar de nuevo
              </button>
              
              <button
                onClick={handleGenerateImprovedWord}
                disabled={analyzing}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: analyzing ? '#cbd5e1' : '#059669',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: analyzing ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!analyzing) e.target.style.backgroundColor = '#047857';
                }}
                onMouseLeave={(e) => {
                  if (!analyzing) e.target.style.backgroundColor = '#059669';
                }}
              >
                {analyzing ? '⏳ Generando...' : '✅ Generar CV mejorado'}
              </button>
            </div>

            <AnalyzingProgress
              active={analyzing}
              title={t('flow.generateProgressTitle')}
              steps={[
                t('flow.generateStep1'),
                t('flow.generateStep2'),
                t('flow.generateStep3'),
              ]}
            />
          </div>
        </div>
      )}

      {/* Step 4: Download Options */}
      {step === 4 && (
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '16px',
          padding: '3rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          border: '2px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
          <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#059669', marginBottom: '1rem' }}>
            ¡CV mejorado correctamente!
          </h2>
          
          <p style={{ fontSize: '1.1rem', color: '#6b7280', marginBottom: '2rem' }}>
            Elige el formato de descarga:
          </p>

          {/* Download Buttons */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
            maxWidth: '600px',
            margin: '0 auto 2rem'
          }}>
            {/* Word Download */}
            <div style={{
              backgroundColor: '#f0f9ff',
              borderRadius: '12px',
              padding: '2rem',
              border: '2px solid #3b82f6',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleDownload('docx')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(59, 130, 246, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📝</div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem' }}>
                Descargar Word
              </h3>
              <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1rem' }}>
                {formatMode === 'free' && '🚀 Diseño profesional nuevo'}
                {formatMode === 'extract_styles' && '🎨 Tus colores y tipografías conservados'}
                {formatMode === 'preserve_exact' && '✨ Diseño original conservado'}
              </p>
              <button
                disabled={analyzing}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: analyzing ? '#cbd5e1' : '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: analyzing ? 'not-allowed' : 'pointer'
                }}
              >
                {analyzing ? '⏳ Generando...' : '📥 Descargar .DOCX'}
              </button>
            </div>

            {/* PDF Download */}
            <div style={{
              backgroundColor: '#fef2f2',
              borderRadius: '12px',
              padding: '2rem',
              border: '2px solid #ef4444',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleDownload('pdf')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(239, 68, 68, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📄</div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#dc2626', marginBottom: '0.5rem' }}>
                Descargar PDF
              </h3>
              <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1rem' }}>
                🎨 Formato PDF profesional
              </p>
              <button
                disabled={analyzing}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: analyzing ? '#cbd5e1' : '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: analyzing ? 'not-allowed' : 'pointer'
                }}
              >
                {analyzing ? '⏳ Generando...' : '📥 Descargar .PDF'}
              </button>
            </div>
          </div>

          {/* Start Over Button */}
          <button
            onClick={handleStartOver}
            style={{
              padding: '0.75rem 2rem',
              backgroundColor: '#6b7280',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
          >
            🔄 Empezar de nuevo
          </button>
        </div>
      )}

      {/* Add spinning animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      {upgradePrompt}
    </div>
  );
};

export default WordImprovement;

